"use client";

import * as React from "react";
import { Check, ChevronsUpDown, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Field } from "@/type/common";
import axios from "@/services/axios";
import { refactor_fields } from "@/utils/refactor";

interface FieldPath {
  field: Field;
  path: string;
}

interface FieldSelectorProps {
  value?: Field | null;
  fields: Field[];
  placeholder?: string;
  noFoundPlaceholder?: string;
  onChange: (value: Field) => void;
  className?: string;
}

// Placeholder API call for many2one subfields
const fetchSubFields = async (field: Field): Promise<Field[]> => {
  const fieldsData = await axios
    .post("/odash/execute", { action: "get_model_fields", parameters: { model_name: field.model } })
    .then((e) => e.data)
    .catch((_e) => null);
  if (fieldsData) {
    const refactoredFields = refactor_fields(fieldsData) as any;
    return refactoredFields;
  }
  return [];
};

const FieldSelector = ({
  value = null,
  fields,
  placeholder = "Select field...",
  noFoundPlaceholder = "No fields found...",
  onChange,
  className = "",
}: FieldSelectorProps) => {
  const [open, setOpen] = React.useState(false);
  const [currentValue, setCurrentValue] = React.useState<Field | null>(value);
  const [fieldPath, setFieldPath] = React.useState<FieldPath[]>([]);
  const [currentFields, setCurrentFields] = React.useState<Field[]>(fields);
  const [loading, setLoading] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const breadcrumbRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);

  // Sync currentValue with value prop changes
  React.useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  // Auto-navigate to the default value when component mounts
  React.useEffect(() => {
    if (value?.field && !initialized && fields.length > 0) {
      setInitialized(true);
      navigateToPath(value.field);
    }
  }, [value, fields, initialized]);

  const navigateToPath = async (path: string) => {
    if (!path) return;

    const pathParts = path.split(".");
    let currentFieldList = fields;
    const newFieldPath: FieldPath[] = [];

    for (let i = 0; i < pathParts.length; i++) {
      const fieldName = pathParts[i];
      const field = currentFieldList.find((f) => f.field === fieldName);

      if (!field) break;

      // Only add to fieldPath if this is a many2one field that we're navigating INTO
      // (not the final field in the path)
      if (i < pathParts.length - 1 && field.type === "many2one") {
        const currentPath = pathParts.slice(0, i + 1).join(".");
        newFieldPath.push({ field, path: currentPath });

        setLoading(true);
        try {
          currentFieldList = await fetchSubFields(field);
        } catch (error) {
          console.error("Failed to fetch subfields:", error);
          break;
        } finally {
          setLoading(false);
        }
      } else if (i === pathParts.length - 1) {
        // This is the final field - don't add it to navigation path
        // Just make sure we're showing the right field list for selection
        break;
      } else {
        // This is a non-many2one field in the middle of a path - shouldn't happen
        console.warn(
          `Non-many2one field '${fieldName}' found in middle of path '${path}'`
        );
        break;
      }
    }

    setFieldPath(newFieldPath);
    setCurrentFields(currentFieldList);
    setSearchValue(""); // Reset search when navigating
  };

  const selectField = (field: Field) => {
    // Select the field directly
    const newPath =
      fieldPath.length > 0
        ? `${fieldPath[fieldPath.length - 1].path}.${field.field}`
        : field.field;

    const selectedField: Field = {
      ...field,
      field: newPath,
      value: newPath,
      search: newPath,
    };

    setCurrentValue(selectedField);
    onChange(selectedField);
    setOpen(false);
  };

  const navigateIntoField = async (field: Field) => {
    // Navigate into the many2one field
    setLoading(true);
    try {
      const subFields = await fetchSubFields(field);
      const newPath =
        fieldPath.length > 0
          ? `${fieldPath[fieldPath.length - 1].path}.${field.field}`
          : field.field;

      setFieldPath([...fieldPath, { field, path: newPath }]);
      setCurrentFields(subFields);
      setSearchValue(""); // Reset search when navigating into many2one

      setTimeout(() => {
        ref.current?.scrollTo({ top: 0 });
      }, 50);

      // Scroll breadcrumbs to the right to show the latest navigation
      setTimeout(() => {
        if (breadcrumbRef.current) {
          breadcrumbRef.current.scrollLeft = breadcrumbRef.current.scrollWidth;
        }
      }, 100);
    } catch (error) {
      console.error("Failed to fetch subfields:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateBack = async (index: number) => {
    if (index === -1) {
      // Navigate back to root
      setFieldPath([]);
      setCurrentFields(fields);
      setSearchValue(""); // Reset search when navigating back
      setTimeout(() => {
        ref.current?.scrollTo({ top: 0 });
      }, 50);
    } else {
      // Navigate back to specific level
      const newFieldPath = fieldPath.slice(0, index + 1);
      setFieldPath(newFieldPath);

      if (index === 0) {
        // Going back to first level after root
        setCurrentFields(fields);
      } else {
        // Need to fetch fields for the target level
        const targetField = newFieldPath[index - 1].field;
        if (targetField.type === "many2one") {
          setLoading(true);
          try {
            const subFields = await fetchSubFields(targetField);
            setCurrentFields(subFields);
          } catch (error) {
            console.error("Failed to fetch subfields:", error);
            // Fallback to root if fetch fails
            setFieldPath([]);
            setCurrentFields(fields);
          } finally {
            setLoading(false);
          }
        }
      }
      setSearchValue(""); // Reset search when navigating back
    }
  };

  const getCurrentDisplayValue = () => {
    if (!currentValue) return placeholder;

    // Use the label if available, otherwise fall back to name
    const displayName = currentValue.label || currentValue.name;

    // If it's a nested field (contains dots), show it with navigation indicator
    if (currentValue.field.includes(".")) {
      // Extract just the final field name for cleaner display
      const pathParts = currentValue.field.split(".");
      const finalFieldName = pathParts[pathParts.length - 1];

      // Try to get a better display name for the final field
      const finalDisplayName = displayName.includes(" → ")
        ? displayName.split(" → ").pop()
        : displayName;

      return `⋯ → ${finalDisplayName}`;
    }

    // For simple fields, just show the display name
    return displayName;
  };

  const getBreadcrumbPath = () => {
    const breadcrumbs = [{ label: "Fields", onClick: () => navigateBack(-1) }];

    fieldPath.forEach((pathItem, index) => {
      breadcrumbs.push({
        label: pathItem.field.label || pathItem.field.name,
        onClick: () => navigateBack(index),
      });
    });

    return breadcrumbs;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${className}`}
        >
          <span className="truncate max-w-[200px]">
            {getCurrentDisplayValue()}
          </span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[400px]">
        <Command>
          {/* Breadcrumb navigation */}
          {fieldPath.length > 0 && (
            <div
              ref={breadcrumbRef}
              className="flex items-center gap-1 p-2 border-b bg-muted/50 overflow-x-auto max-w-full"
              style={{ scrollbarWidth: "thin" }}
            >
              {getBreadcrumbPath().map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs whitespace-nowrap flex-shrink-0"
                    onClick={crumb.onClick}
                  >
                    {crumb.label}
                  </Button>
                </React.Fragment>
              ))}
            </div>
          )}

          <CommandInput
            placeholder={`Search ${fieldPath.length > 0 ? "sub" : ""}fields...`}
            className="h-9"
            value={searchValue}
            onValueChange={setSearchValue}
            onChangeCapture={(e) => {
              setTimeout(() => {
                ref.current?.scrollTo({ top: 0 });
              }, 50);
            }}
          />
          <CommandList ref={ref}>
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Loading fields...
              </div>
            ) : (
              <>
                <CommandEmpty>{noFoundPlaceholder}</CommandEmpty>
                <CommandGroup>
                  {currentFields.map((field) => (
                    <CommandItem
                      key={field.field}
                      value={field.search}
                      onSelect={() => selectField(field)}
                      className="flex items-center justify-between cursor-pointer group"
                    >
                      <div className="flex flex-col flex-1">
                        <span>{field.label || field.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {field.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Check
                          className={cn(
                            "h-4 w-4",
                            currentValue?.field ===
                              (fieldPath.length > 0
                                ? `${fieldPath[fieldPath.length - 1].path}.${
                                    field.field
                                  }`
                                : field.field)
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        {field.type === "many2one" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateIntoField(field);
                            }}
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default FieldSelector;
